/**
 * StripeStatusPanel — Realtime Stripe verbindings- en betaal-status.
 *
 * Onderdeel van Instellingen → Betalingen (Stripe) tabblad.  Verzamelt de
 * data uit twee endpoints:
 *   GET /api/admin/stripe/status      — mode, keys, mode-mismatch, webhook
 *   GET /api/admin/stripe/dashboard   — omzet + laatste transacties
 *
 * Ook een test-connection knop die live tegen Stripe.Balance.retrieve()
 * praat en accountland + charges/payouts terugmeldt.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import {
  RefreshCw, Loader2, ShieldCheck, ShieldAlert, KeyRound, Zap,
  CheckCircle2, XCircle, CreditCard, Send, AlertTriangle, Globe,
} from "lucide-react";
import { AdminKpiCard, AdminSectionCard, AdminStatusBadge, AdminSkeleton } from "../ui";

function fmtMoney(v, cur = "EUR") {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: (cur || "EUR").toUpperCase(),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${cur} ${Number(v).toFixed(2)}`; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function StripeStatusPanel() {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        adminApi.get("/admin/stripe/status"),
        adminApi.get("/admin/stripe/dashboard"),
      ]);
      setStatus(s.data);
      setDashboard(d.data);
    } catch (e) {
      toast.error(formatApiError(e, "Stripe status laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await adminApi.post("/admin/stripe/status/test-connection");
      setTestResult(r.data);
      if (r.data.status === "ok") toast.success(`Verbonden met Stripe (${r.data.mode})`);
      else toast.warning(r.data.message || "Verbinding gaf een probleem terug.");
    } catch (e) {
      toast.error(formatApiError(e, "Verbindingstest mislukt"));
    } finally { setTesting(false); }
  };

  if (loading || !status) {
    return <AdminSkeleton variant="kpi-grid" count={4} />;
  }

  const mode = status.keys?.secret_mode || "missing";
  const prod = status.environment?.production;
  const prodReady = status.production_guard?.production_ready;
  const blockers = status.production_guard?.blockers || [];
  const modeMismatch = !!status.keys?.mode_mismatch;

  return (
    <div className="space-y-5" data-testid="stripe-status-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-chivo font-bold text-lg text-brand-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-brand-500" /> Stripe Status
          </h3>
          <p className="text-xs text-ink-500">
            Realtime verbindings- en betaalmodus. Kaal & inzichtelijk.
            {status.as_of && <> · Peildatum {fmtDate(status.as_of)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" onClick={load} disabled={loading}
            className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500"
            data-testid="stripe-status-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Vernieuwen
          </Button>
          <Button
            onClick={runTest} disabled={testing}
            className="rounded-full bg-brand-900 hover:bg-brand-700 text-white shadow-brand-soft"
            data-testid="stripe-status-test-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
            Verbinding testen
          </Button>
        </div>
      </div>

      {/* Production Guard banner */}
      {prod ? (
        prodReady ? (
          <div className="border-l-4 border-emerald-500 bg-emerald-50 p-4 flex items-start gap-3" data-testid="stripe-status-prod-ok">
            <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <div className="font-chivo font-bold">Productie klaar</div>
              Live keys geconfigureerd, webhook secret aanwezig. De Checkout draait in live mode zonder Sandbox-label.
            </div>
          </div>
        ) : (
          <div className="border-l-4 border-red-500 bg-red-50 p-4 flex items-start gap-3" data-testid="stripe-status-prod-blocked">
            <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <div className="font-chivo font-bold">Production Guard — betalingen geblokkeerd</div>
              <ul className="mt-1 list-disc pl-5 space-y-0.5">
                {blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
              Vul een Live Secret Key (sk_live_…) in via het Stripe-tabblad hierboven om verder te gaan.
            </div>
          </div>
        )
      ) : (
        <div className="border-l-4 border-blue-500 bg-blue-50 p-3 flex items-start gap-3 text-sm text-blue-900" data-testid="stripe-status-preview-note">
          <Globe className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Preview-omgeving — Production Guard is niet actief. Test-keys mogen hier gebruikt worden.</div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <AdminKpiCard
          label="Mode"
          value={mode === "live" ? "LIVE" : mode === "test" ? "TEST" : "ONBEKEND"}
          icon={Zap}
          tone={mode === "live" ? "emerald" : mode === "test" ? "blue" : "slate"}
          sublabel={modeMismatch ? "⚠ mode-mismatch met publishable" : status.keys?.secret_preview?.preview}
          testid="stripe-kpi-mode"
        />
        <AdminKpiCard
          label="Omzet vandaag"
          value={fmtMoney(dashboard?.revenue_today)}
          icon={CreditCard}
          tone="emerald"
          sublabel={`${dashboard?.payments_today_count || 0} betalingen`}
          testid="stripe-kpi-revenue-today"
        />
        <AdminKpiCard
          label="Omzet deze maand"
          value={fmtMoney(dashboard?.revenue_this_month)}
          icon={CreditCard}
          tone="brand"
          sublabel={`${dashboard?.payments_this_month_count || 0} betalingen`}
          testid="stripe-kpi-revenue-month"
        />
        <AdminKpiCard
          label="Open sessies"
          value={dashboard?.open_checkout_sessions ?? 0}
          icon={Send}
          tone="amber"
          sublabel={`${dashboard?.expired_checkout_sessions ?? 0} verlopen`}
          testid="stripe-kpi-open-sessions"
        />
      </div>

      {/* Configuration check */}
      <AdminSectionCard title="Configuratie" testid="stripe-status-config">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <ConfigRow
            label="Secret Key"
            configured={status.keys?.secret_configured}
            preview={status.keys?.secret_preview?.preview}
            mode={status.keys?.secret_mode}
          />
          <ConfigRow
            label="Publishable Key"
            configured={status.keys?.publishable_configured}
            preview={status.keys?.publishable_preview?.preview}
            mode={status.keys?.publishable_mode}
          />
          <ConfigRow
            label="Webhook Secret"
            configured={status.keys?.webhook_secret_configured}
            preview={status.keys?.webhook_secret_preview?.preview}
          />
        </div>
        {modeMismatch && (
          <div className="mt-3 border-l-4 border-orange-400 bg-orange-50 p-3 text-xs text-orange-900" data-testid="stripe-status-mode-mismatch">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Mode-mismatch: secret={status.keys?.secret_mode} maar publishable={status.keys?.publishable_mode}. Beide moeten identiek zijn.
          </div>
        )}
      </AdminSectionCard>

      {/* Webhook & payments overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AdminSectionCard title="Webhook activiteit" testid="stripe-status-webhook">
          <ul className="text-sm space-y-2">
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Laatste ontvangen</span>
              <span className="font-mono-num tabular-nums text-brand-900">{fmtDate(status.webhook?.last_event_at)}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Laatste type</span>
              <span className="text-brand-900">{status.webhook?.last_event_type || "—"}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Laatste mislukte</span>
              <span className="font-mono-num tabular-nums text-red-700">
                {fmtDate(status.webhook?.last_failed_at)}
                {status.webhook?.last_failed_type && <span className="ml-1 text-xs text-ink-500">({status.webhook.last_failed_type})</span>}
              </span>
            </li>
          </ul>
        </AdminSectionCard>

        <AdminSectionCard title="Laatste succesvolle betaling" testid="stripe-status-last-payment">
          <ul className="text-sm space-y-2">
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Datum</span>
              <span className="font-mono-num tabular-nums text-brand-900">{fmtDate(status.payments?.last_successful_at)}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Bedrag</span>
              <span className="font-mono-num tabular-nums font-semibold text-emerald-700">
                {fmtMoney(status.payments?.last_successful_amount, status.payments?.last_successful_currency)}
              </span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-ink-500">Fouten (laatste 24u)</span>
              <span className={`font-mono-num tabular-nums ${dashboard?.failed_events_last_24h ? "text-red-700 font-bold" : "text-ink-400"}`}>
                {dashboard?.failed_events_last_24h ?? 0}
              </span>
            </li>
          </ul>
        </AdminSectionCard>
      </div>

      {/* Recent transactions */}
      {dashboard?.recent_payments?.length > 0 && (
        <AdminSectionCard title="Laatste transacties" padded={false} testid="stripe-status-recent">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-chivo font-bold">
              <tr>
                <th className="text-left px-5 py-3">Datum</th>
                <th className="text-left px-5 py-3">Factuur</th>
                <th className="text-left px-5 py-3">Klant</th>
                <th className="text-right px-5 py-3">Bedrag</th>
                <th className="text-left px-5 py-3">Referentie</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.recent_payments.map((p, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-brand-50/40">
                  <td className="px-5 py-3 text-ink-500 font-mono-num tabular-nums">{p.payment_date || "—"}</td>
                  <td className="px-5 py-3 font-mono-num tabular-nums text-brand-900">{p.invoice_number || "—"}</td>
                  <td className="px-5 py-3 text-ink-700">{p.customer_name || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono-num tabular-nums font-semibold text-emerald-700">{fmtMoney(p.amount, p.currency)}</td>
                  <td className="px-5 py-3 text-xs text-ink-400 truncate max-w-[200px]">{p.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminSectionCard>
      )}

      {/* Test-connection result */}
      {testResult && (
        <div
          className={`border-l-4 p-4 text-sm ${
            testResult.status === "ok"
              ? "border-emerald-500 bg-emerald-50 text-emerald-900"
              : "border-amber-500 bg-amber-50 text-amber-900"
          }`}
          data-testid="stripe-status-test-result"
        >
          <div className="font-chivo font-bold flex items-center gap-2">
            {testResult.status === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            Resultaat: {testResult.status}
          </div>
          <div className="mt-1">{testResult.message}</div>
          {testResult.status === "ok" && (
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 list-none text-xs">
              <li>Mode: <b>{testResult.mode}</b></li>
              {testResult.account_id && <li>Account: <b>{testResult.account_id.slice(0, 12)}…</b></li>}
              {testResult.account_country && <li>Land: <b>{testResult.account_country}</b></li>}
              <li>Charges: <b>{String(testResult.charges_enabled ?? "—")}</b></li>
              <li>Payouts: <b>{String(testResult.payouts_enabled ?? "—")}</b></li>
              <li>Live: <b>{String(testResult.livemode ?? "—")}</b></li>
              {testResult.api_version && <li>API-versie: <b>{testResult.api_version}</b></li>}
            </ul>
          )}
          {testResult.detail && <div className="mt-2 font-mono text-[11px] break-all">{testResult.detail}</div>}
        </div>
      )}
    </div>
  );
}

function ConfigRow({ label, configured, preview, mode }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
      <div className="text-[10px] uppercase tracking-[0.14em] text-ink-500 font-chivo font-bold">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        {configured ? (
          <AdminStatusBadge tone="success" size="xs" label="Ingesteld" icon={CheckCircle2} />
        ) : (
          <AdminStatusBadge tone="danger" size="xs" label="Ontbreekt" icon={XCircle} />
        )}
        {mode && (
          <AdminStatusBadge
            tone={mode === "live" ? "success" : mode === "test" ? "info" : "muted"}
            size="xs"
            label={mode.toUpperCase()}
          />
        )}
      </div>
      {preview && (
        <div className="mt-2 text-[11px] font-mono text-ink-500 truncate" title={preview}>{preview}</div>
      )}
    </div>
  );
}
